export type AnyValue = any;

/** The domain of values that can flow through the unit pipeline at any stage. */
export type RawValue = number | string | boolean | null | undefined;

/** Anything a caller might pass as formatUnits' value — RawValue plus arbitrary
 *  non-primitive shapes (e.g. Schedule/Sequence objects from settings) that
 *  formatUnits coerces to `undefined` before use. */
export type FormatUnitsValue = RawValue | object;

export type FormatUnitsInput = {
  value: FormatUnitsValue;
  units?: string;
};

/** Raw, as-received input: units may be entirely absent. Public API boundary
 *  (formatUnits/transformUnits params). */
export type UnitValue = {
  value: RawValue;
  units?: string;
};

/** Post-normalization: units is always a resolved, defined string (possibly '').
 *  What transform functions operate on — lets transforms drop defensive
 *  `unitValue.units ?? ''` / isNullOrUndefined checks entirely. */
export type NormalizedUnitValue = {
  value: RawValue;
  units: string;
};

/** Input to a registry formatter. The value has already been transformed by
 *  formatUnits(); labels come from catalog config or the formatter's own constants. */
export type FormatterInput = {
  value: RawValue;
};

/** Numeric bespoke formatter input after formatUnits() has validated the value. */
export type NumericFormatterInput = {
  value: number;
};

/** Fallback formatter input for units with no catalog entry — the only place a
 *  label is supplied at call time. */
export type DynamicLabelFormatterInput = {
  value: number;
  unitLabel: string;
};

export type UnitFormatterType = (input: FormatterInput) => FormattedUnitLabels;

export type UnitTransformerType = (
  unitValue: NormalizedUnitValue,
  newUnits?: string
) => NormalizedUnitValue;

export type FormattedUnitLabels = {
  /** Numeric string only — `Number()`-safe for editable inputs and write-back. */
  valueLabel: string;
  /** Full unit token including prefix (e.g. `'$/kWh'`, `'$'`, `'°F'`). */
  unitsLabel: string;
  /** Display value with prefix when configured (e.g. `'$150'`); equals `valueLabel` when no prefix. */
  prefixedValueLabel: string;
  /** Suffix only (e.g. `'/kWh'`, `'°F'`, `''`); equals `unitsLabel` when no prefix. */
  unitSuffixLabel: string;
  /** Numeric value joined to suffix only, no prefix (e.g. `'0.1/kWh'`, `'100.12 °F'`). Pairs with `prefixedValueLabel`. */
  suffixedValueLabel: string;
  valueUnitsLabel: string;
};

// --- Catalog types ---

type PrecisionFormatterConfigBase = {
  /** Decimal places to round to */
  precision: number;
  /** When true, concatenates value and unit with no separator ('45°'). Default: false ('12 MPH'). */
  noSpace?: boolean;
};

/** Runtime formatter config — used by formatNumberDefault fallback (no label required). */
export type PrecisionFormatterConfig = PrecisionFormatterConfigBase & {
  /** Prepended to the formatted numeric value (e.g. '$' for currency). */
  unitPrefix?: string;
  /** Rendered unit suffix (e.g. '°C', '/kWh'). */
  unitSuffix?: string;
};

/** Catalog precision config — at least one of unitPrefix or unitSuffix must be non-empty. */
export type CatalogPrecisionFormatterConfig = PrecisionFormatterConfigBase &
  ({ unitPrefix: string; unitSuffix?: string } | { unitPrefix?: string; unitSuffix: string });

/** Bespoke formatter registered in the catalog — exempt fns use FormatterInput; numeric bespoke use NumericFormatterInput (registry wraps). */
export type UnitCatalogFormatterFn =
  | ((input: FormatterInput) => FormattedUnitLabels)
  | ((input: NumericFormatterInput) => FormattedUnitLabels);

export type UnitCatalogEntry = {
  /** Required unique catalog identifier — ATLAS Units V2 Unit Name (or catalog-only id). */
  name: string;
  /** Required ASCII-safe wire/stored form (ATLAS CSV ASCII-Safe, or catalog-only slug). */
  asciiSymbol: string;
  /** Additional case-insensitive lookup keys; do not repeat name or asciiSymbol. */
  aliases?: string[];
  /** Family-conversion function for units with a real numeric conversion (e.g.
   *  °C↔°F, hrs↔min↔sec). Omit for units with no conversions. */
  transform?: (unitValue: NormalizedUnitValue, newUnits?: string) => NormalizedUnitValue;
  /** For presentation entries whose formatter reads a specific unit (duration reads
   *  seconds, direction reads degrees): the unit formatUnits converts to before
   *  formatting. Omit when the formatter reads the value as-is. */
  inputUnits?: string;
  /** Generic precision formatter config or bespoke formatting function. */
  formatter: CatalogPrecisionFormatterConfig | UnitCatalogFormatterFn;
};

export function isCatalogPrecisionFormatterConfig(
  formatter: UnitCatalogEntry['formatter']
): formatter is CatalogPrecisionFormatterConfig {
  return typeof formatter !== 'function';
}
